import * as React from "react";
import { useDispatch } from "react-redux";
import {
    alpha,
    Box,
    Chip,
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { schoolOffersBoarding } from "../../utils/boardingUtils";
import { useCloseMobileNav, useNavCollapsed } from "../../components/DashboardLayoutContext";
import { ADMIN_HOSTEL_SECTION, getAdminNavSections } from "../../config/adminWorkflow";
import { useFeatures } from "../../context/FeatureContext";
import { useSelector } from "react-redux";
import { adminFinanceManagedByBursar, adminLibraryManagedByLibrarian } from "../../utils/staffRoles";
import { getAllTeachers } from "../../redux/teacherRelated/teacherHandle";

const SECTION_ICONS = {
    start: HomeOutlinedIcon,
    setup: SchoolOutlinedIcon,
    students: PersonOutlineIcon,
    daily: AnnouncementOutlinedIcon,
    exams: GradingIcon,
    fees: PaymentsOutlinedIcon,
    expenses: ReceiptLongOutlinedIcon,
    planning: CalendarMonthOutlinedIcon,
    hostels: HotelOutlinedIcon,
    library: LocalLibraryOutlinedIcon,
    infrastructure: BusinessOutlinedIcon,
};

const ITEM_ICONS = {
    "/Admin/dashboard": HomeOutlinedIcon,
    "/Admin/analytics": TrendingUpOutlinedIcon,
    "/Admin/emis-reports": DescriptionOutlinedIcon,
    "/Admin/addclass": PostAddOutlinedIcon,
    "/Admin/classes": ClassOutlinedIcon,
    "/Admin/subjects/chooseclass": AssignmentOutlinedIcon,
    "/Admin/teachers": SupervisorAccountOutlinedIcon,
    "/Admin/school-page": WebOutlinedIcon,
    "/Admin/students/chooseclass": PersonAddAlt1Icon,
    "/Admin/students": PersonOutlineIcon,
    "/Admin/complains": ReportOutlinedIcon,
    "/Admin/addnotice": AnnouncementOutlinedIcon,
    "/Admin/notices": AnnouncementOutlinedIcon,
    "/Admin/attendance/review": EventAvailableOutlinedIcon,
    "/Admin/staff/attendance": AccessTimeOutlinedIcon,
    "/Admin/exams/review": FactCheckOutlinedIcon,
    "/Admin/exams/marks-review": FactCheckOutlinedIcon,
    "/Admin/enrollment/review": PersonAddAlt1Icon,
    "/Admin/students/rollover": TrendingUpOutlinedIcon,
    "/Admin/schemes": MenuBookOutlinedIcon,
    "/Admin/leaves": EventBusyOutlinedIcon,
    "/Admin/disciplinary": WarningAmberOutlinedIcon,
    "/Admin/classes/results/overview": GradingIcon,
    "/Admin/curriculum": SchoolOutlinedIcon,
    "/Admin/maneb": SchoolOutlinedIcon,
    "/Admin/fees/structures": AccountBalanceOutlinedIcon,
    "/Admin/fees/payment-settings": AccountBalanceOutlinedIcon,
    "/Admin/fees/payment-submissions": PaymentsOutlinedIcon,
    "/Admin/fees/collection": PaymentsOutlinedIcon,
    "/Admin/fees/defaulters": WarningAmberOutlinedIcon,
    "/Admin/fees/reports": BarChartOutlinedIcon,
    "/Admin/expenses/log": ReceiptLongOutlinedIcon,
    "/Admin/expenses/budgets": SavingsOutlinedIcon,
    "/Admin/expenses/summary": AssessmentOutlinedIcon,
    "/Admin/timetables": AccessTimeOutlinedIcon,
    "/Admin/calendar": CalendarMonthOutlinedIcon,
    "/Admin/hostels": HotelOutlinedIcon,
    "/Admin/hostels/boarders": PersonOutlineIcon,
    "/Admin/library": LocalLibraryOutlinedIcon,
    "/Admin/library/overview": FolderSharedOutlinedIcon,
    "/Admin/infrastructure": BusinessOutlinedIcon,
    "/Admin/profile": AccountCircleOutlinedIcon,
    "/logout": ExitToAppIcon,
    "/docs": MenuBookOutlinedIcon,
};

function groupActive(items, path) {
    return items.some((i) => i.match(path));
}

function NavRow({ to, label, hint, Icon, selected, tone, nested, external, moduleDisabled = false }) {
    const isDanger = tone === "danger";
    const dense = Boolean(nested);
    const closeMobileNav = useCloseMobileNav();
    const collapsed = useNavCollapsed();
    const ItemIcon = Icon || CircleOutlinedIcon;
    const disabled = moduleDisabled;

    const handleClick = (event) => {
        if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        closeMobileNav();
    };

    const linkProps = disabled
        ? {}
        : external
          ? { component: "a", href: to, target: "_blank", rel: "noopener noreferrer" }
          : { component: Link, to };

    return (
        <ListItemButton
            title={collapsed ? label : undefined}
            {...linkProps}
            selected={selected && !disabled}
            onClick={handleClick}
            aria-disabled={disabled}
            sx={{
                borderRadius: 2,
                mx: 0.5,
                mb: 0.2,
                py: dense ? 0.65 : 0.85,
                px: dense ? 1 : 1.15,
                pl: dense ? 2 : 1.15,
                overflow: "hidden",
                maxWidth: "100%",
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                pointerEvents: disabled ? "auto" : undefined,
                "& .MuiListItemText-root": {
                    minWidth: 0,
                    overflow: "hidden",
                },
                "& .MuiListItemText-primary": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                },
                "&.Mui-selected": {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: "primary.main",
                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                },
                ...(isDanger && {
                    "&.Mui-selected": {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                        color: "error.main",
                    },
                }),
            }}
        >
            <ListItemIcon sx={{ minWidth: collapsed ? 40 : dense ? 34 : 38, justifyContent: "center" }}>
                <ItemIcon sx={{ fontSize: dense ? "1.05rem" : "1.15rem", opacity: selected && !disabled ? 1 : 0.75 }} />
            </ListItemIcon>
            {!collapsed ? (
                <ListItemText
                    primary={
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontWeight: selected && !disabled ? 700 : 500,
                                    fontSize: dense ? "0.8125rem" : "0.875rem",
                                }}
                            >
                                {label}
                            </Box>
                            {disabled && !moduleDisabled ? (
                                <Chip
                                    label="Disabled"
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                    }}
                                />
                            ) : null}
                        </Stack>
                    }
                    secondary={hint}
                    secondaryTypographyProps={{ fontSize: 10, lineHeight: 1.2, sx: { mt: 0.1 } }}
                />
            ) : null}
        </ListItemButton>
    );
}

function NavGroup({ section, path }) {
    const { title, summary, stepLabel, items, id } = section;
    const { mode, isModuleEnabled } = useFeatures();
    const moduleDisabled = mode === "admin-only" && !isModuleEnabled(id);
    const GroupIcon = SECTION_ICONS[id] || SchoolOutlinedIcon;
    const active = groupActive(items, path);
    const collapsed = useNavCollapsed();
    const [open, setOpen] = React.useState(() => active || section.defaultOpen);

    React.useEffect(() => {
        if (active) setOpen(true);
    }, [active, path]);

    if (collapsed && id !== "start") {
        return (
            <Box sx={{ px: 0.5, mb: 0.25, opacity: moduleDisabled ? 0.5 : 1 }}>
                {items.map((item) => (
                    <NavRow
                        key={item.to}
                        to={item.to}
                        label={item.label}
                        hint={item.hint}
                        Icon={ITEM_ICONS[item.to] || GroupIcon}
                        selected={item.match(path)}
                        moduleDisabled={moduleDisabled}
                    />
                ))}
            </Box>
        );
    }

    if (id === "start") {
        return (
            <Box sx={{ px: 1, mb: 0.5, opacity: moduleDisabled ? 0.5 : 1 }}>
                {moduleDisabled && !collapsed ? (
                    <Chip
                        label="Module disabled"
                        size="small"
                        sx={{ ml: 1, mb: 0.5, height: 20, fontSize: 10, fontWeight: 700 }}
                    />
                ) : null}
                {items.map((item) => (
                    <NavRow
                        key={item.to}
                        to={item.to}
                        label={item.label}
                        hint={item.hint}
                        Icon={ITEM_ICONS[item.to]}
                        selected={item.match(path)}
                        moduleDisabled={moduleDisabled}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ px: 1, mb: 0.25, opacity: moduleDisabled ? 0.5 : 1 }}>
            <ListItemButton
                onClick={() => {
                    if (!moduleDisabled) setOpen((v) => !v);
                }}
                aria-disabled={moduleDisabled}
                sx={{
                    borderRadius: 2,
                    py: 0.75,
                    px: 1.1,
                    mb: 0.25,
                    cursor: moduleDisabled ? "not-allowed" : "pointer",
                    bgcolor: (t) => alpha(t.palette.primary.main, active && !moduleDisabled ? 0.1 : 0.03),
                }}
            >
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <GroupIcon
                        sx={{
                            fontSize: "1.15rem",
                            color: active && !moduleDisabled ? "primary.main" : "text.secondary",
                        }}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            {stepLabel ? (
                                <Chip
                                    label={stepLabel}
                                    size="small"
                                    sx={{ height: 18, fontSize: 9, fontWeight: 800 }}
                                    color={active && !moduleDisabled ? "primary" : "default"}
                                />
                            ) : null}
                            <span>{title}</span>
                            {moduleDisabled ? (
                                <Chip
                                    label="Disabled"
                                    size="small"
                                    sx={{ height: 18, fontSize: 9, fontWeight: 700 }}
                                />
                            ) : null}
                        </Stack>
                    }
                    secondary={summary}
                    primaryTypographyProps={{
                        component: "div",
                        variant: "body2",
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                    }}
                    secondaryTypographyProps={{ variant: "caption", sx: { fontSize: 10, lineHeight: 1.2 } }}
                />
                <ExpandMoreIcon
                    sx={{
                        fontSize: 22,
                        transform: open ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s",
                    }}
                />
            </ListItemButton>
            <Collapse in={open}>
                <List disablePadding sx={{ pt: 0.25, pb: 0.5 }}>
                    {items.map((item) => (
                        <NavRow
                            key={item.to}
                            to={item.to}
                            label={item.label}
                            hint={item.hint}
                            Icon={ITEM_ICONS[item.to]}
                            selected={item.match(path)}
                            nested
                            moduleDisabled={moduleDisabled}
                        />
                    ))}
                </List>
            </Collapse>
        </Box>
    );
}

const accountNav = [
    {
        to: "/Admin/profile",
        label: "My profile",
        hint: "Your details & password",
        match: (p) => p.startsWith("/Admin/profile"),
    },
    {
        to: "/docs/getting-started",
        label: "Documentation",
        hint: "How-to guides and module walkthroughs",
        match: () => false,
        external: true,
    },
    {
        to: "/logout",
        label: "Log out",
        hint: "Sign out safely",
        match: (p) => p.startsWith("/logout"),
        tone: "danger",
    },
];

const SideBar = () => {
    const location = useLocation();
    const path = location.pathname;
    const collapsed = useNavCollapsed();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { teachersList } = useSelector((state) => state.teacher);

    React.useEffect(() => {
        if (currentUser?._id && (!Array.isArray(teachersList) || teachersList.length === 0)) {
            dispatch(getAllTeachers(currentUser._id));
        }
    }, [currentUser?._id, dispatch, teachersList]);
    const showHostel = schoolOffersBoarding(currentUser?.boardingMode, currentUser?.hasBoarding);
    const bursarManagesFinance = adminFinanceManagedByBursar(currentUser, teachersList, {
        requireProfileCompleted: true,
    });
    const librarianManagesLibrary = adminLibraryManagedByLibrarian(currentUser, teachersList, {
        requireProfileCompleted: true,
    });

    const sections = getAdminNavSections(currentUser?.schoolType)
        .filter((section) => !(librarianManagesLibrary && section.id === "library"))
        .map((section) => {
        if (!bursarManagesFinance || (section.id !== "fees" && section.id !== "expenses")) {
            return section;
        }
        const bursarHint = (hint) =>
            hint ? `${hint} · also in bursar portal` : "View live data · bursar records entries";
        return {
            ...section,
            summary:
                section.id === "fees"
                    ? "Director verifies bursar finance updates"
                    : "Director verifies bursar expense updates",
            items: section.items.map((item) => ({ ...item, hint: bursarHint(item.hint) })),
        };
    })
        .map((section) => {
            if (section.id !== "start" || !librarianManagesLibrary) return section;
            return {
                ...section,
                items: section.items.map((item) =>
                    item.to === "/Admin/analytics"
                        ? { ...item, hint: "Trends, at-risk students, library insights, exports" }
                        : item,
                ),
            };
        });
    if (showHostel) {
        const feesIdx = sections.findIndex((s) => s.id === "expenses");
        sections.splice(feesIdx + 1, 0, ADMIN_HOSTEL_SECTION);
    }

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                borderRight: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
            }}
        >
            {!collapsed ? (
                <>
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            School office menu
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                            Step-by-step guide
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Same order as paper registers & fee books
                        </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.6 }} />
                </>
            ) : null}
            <List component="nav" disablePadding sx={{ flex: 1, py: 1, overflow: "auto" }}>
                {sections.map((section) => (
                    <NavGroup key={section.id} section={section} path={path} />
                ))}
                <Divider sx={{ my: 1, mx: collapsed ? 0.5 : 2 }} />
                {!collapsed ? (
                    <Typography variant="overline" sx={{ px: 2, fontSize: 9, fontWeight: 800, color: "text.secondary" }}>
                        Account
                    </Typography>
                ) : null}
                <Box sx={{ px: 1, pb: 1 }}>
                    {accountNav.map((item) => (
                        <NavRow
                            key={item.to}
                            to={item.to}
                            label={item.label}
                            hint={item.hint}
                            Icon={ITEM_ICONS[item.to]}
                            selected={item.match(path)}
                            tone={item.tone}
                            nested
                            external={item.external}
                        />
                    ))}
                </Box>
            </List>
        </Box>
    );
};

export default SideBar;
